import { memo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Tooltip from "@mui/material/Tooltip";

import type { QuestionnairePayload } from "../../form-builder/types";
import {
  QUESTIONNAIRE_STATUS_TO_UI,
  QUESTIONNAIRE_UNKNOWN_STATUS_TOOLTIP,
  USAGE_TYPE_TO_UI_LABEL,
} from "../utils/questionnaireManagementConstants";

export interface QuestionnaireManagementListProps {
  questionnaires: QuestionnairePayload[];
  isLoading: boolean;
  errorMessage?: string;
  activeId: string | null;
  isDeleting: boolean;
  emptyMessage?: string;
  onSelectQuestionnaire: (questionnaireId: string) => void;
  onEditQuestionnaire: (questionnaireId: string) => void;
  onOpenPreview: (questionnaire: QuestionnairePayload) => void;
  onOpenDelete: (questionnaireId: string) => void;
  onCopyPatientLink?: (questionnaireId: string) => void | Promise<void>;
}

const QuestionnaireManagementList = memo(
  ({
    questionnaires,
    isLoading,
    errorMessage,
    activeId,
    isDeleting,
    emptyMessage = "Nenhum questionário encontrado.",
    onSelectQuestionnaire,
    onEditQuestionnaire,
    onOpenPreview,
    onOpenDelete,
    onCopyPatientLink,
  }: QuestionnaireManagementListProps) => {
    if (isLoading) return <LinearProgress />;
    if (errorMessage) return <Alert severity="error">{errorMessage}</Alert>;

    return (
      <List disablePadding>
        {questionnaires.length === 0 ? (
          <ListItem>
            <ListItemText
              primary={
                <Typography variant="body2" color="text.secondary">
                  {emptyMessage}
                </Typography>
              }
            />
          </ListItem>
        ) : (
          questionnaires.map((questionnaire) => {
            const questionnaireId = questionnaire.id;
            if (!questionnaireId) return null;

            const statusKey =
              questionnaire.status === "active"
                ? "active"
                : questionnaire.status === "unknown"
                  ? "unknown"
                  : "draft";
            const statusUi = QUESTIONNAIRE_STATUS_TO_UI[statusKey];
            const isUnknownStatus = questionnaire.status === "unknown";
            const showCopyPatientLink =
              Boolean(onCopyPatientLink) &&
              (statusKey === "active" || statusKey === "unknown");

            return (
              <ListItem
                key={questionnaireId}
                divider
                button
                selected={activeId === questionnaireId}
                alignItems="flex-start"
                sx={{ py: 1.5 }}
                onClick={() => onSelectQuestionnaire(questionnaireId)}
              >
                <ListItemText
                  primary={
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 0.5 }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>
                        {questionnaire.title}
                      </Typography>
                      <Chip
                        size="small"
                        label={USAGE_TYPE_TO_UI_LABEL[questionnaire.usageType]}
                        color="default"
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={statusUi.label}
                        color={statusUi.chipColor}
                        variant={statusUi.chipVariant}
                      />
                      {isUnknownStatus ? (
                        <Tooltip
                          title={QUESTIONNAIRE_UNKNOWN_STATUS_TOOLTIP}
                          arrow
                        >
                          <ErrorOutlineIcon
                            sx={{
                              fontSize: 22,
                              color: "error.main",
                              display: "block",
                            }}
                            aria-label="Informação sobre versão vista pelos pacientes"
                          />
                        </Tooltip>
                      ) : null}
                    </Stack>
                  }
                  secondary={
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {`${questionnaire.questions.length} perguntas`}
                      </Typography>

                      {questionnaire.description ? (
                        <Box
                          component="span"
                          sx={{
                            display: "-webkit-box",
                            overflow: "hidden",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {questionnaire.description}
                          </Typography>
                        </Box>
                      ) : null}
                    </Stack>
                  }
                />

                <ListItemSecondaryAction>
                  <Stack direction="row" spacing={0.5}>
                    {showCopyPatientLink ? (
                      <Tooltip title="Copiar link do paciente" arrow>
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={(event) => {
                            event.stopPropagation();
                            onCopyPatientLink?.(questionnaireId);
                          }}
                          aria-label="Copiar link do paciente"
                          disabled={isDeleting}
                        >
                          <LinkOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => {
                        onSelectQuestionnaire(questionnaireId);
                        onEditQuestionnaire(questionnaireId);
                      }}
                      aria-label="Editar questionário"
                      disabled={isDeleting}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => {
                        onSelectQuestionnaire(questionnaireId);
                        onOpenPreview(questionnaire);
                      }}
                      aria-label="Visualizar questionário"
                      disabled={isDeleting}
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => {
                        onSelectQuestionnaire(questionnaireId);
                        onOpenDelete(questionnaireId);
                      }}
                      aria-label="Excluir questionário"
                      disabled={isDeleting}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })
        )}
      </List>
    );
  },
);

QuestionnaireManagementList.displayName = "QuestionnaireManagementList";

export default QuestionnaireManagementList;
